import { PageController } from '@client/components/_core/layout/PageController';
import { TextAreaWithAiAssistant } from '@client/components/_shared/ai/TextAreaWithAiAssistant';
import { RecheckSimilarModal, type RecheckTarget } from '@client/components/_shared/auto-detect/RecheckSimilarModal';
import { EmailContentRenderer } from '@client/components/_shared/email/EmailContentRenderer';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CollapsibleCard } from '@client/components/_ui/display/CollapsibleCard';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout';
import { InlineLink } from '@client/components/Common/Link';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useHotkey } from '@client/hooks/useHotkey';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { sanitizeHtml } from '@client/utils';
import { ENABLE_CLASSIFICATION_LEARNING_FLAG } from '@shared/constants';
import { PERMISSIONS } from '@shared/permissions/permissions';
import type { MessageClassificationClaimLinkSource } from '@shared/prisma/enums';
import { createFileRoute } from '@tanstack/react-router';
import JsonView from '@uiw/react-json-view';
import { Alert, Card, Col, Drawer, Form, Row, Select, Skeleton, Switch, Tabs, Tag } from 'antd';
import {
    ChevronLeft,
    ChevronRight,
    Copy,
    Mail,
    MessageSquare,
    MessageSquareText,
    ShieldAlert,
    SlidersHorizontal,
    Sparkles,
    Tags as TagsIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONFIDENCE_MAP } from '../config';
import { useInboxFilterLabel, useInboxSubject, useInboxVisibleIds } from '../inbox-navigation-store';
import { AiSummaryCard } from './AiSummaryCard';
import type { AiOriginalClassification } from './ai-original-classification';
import { ClassificationContextCards } from './ClassificationContextCards';
import { ContactMatchesCard } from './ContactMatchesCard';
import { formatEmailAddresses } from './format-email-addresses';
import { type ClaimDecisionState, LinkedClaimsCard } from './LinkedClaimsCard';
import {
    MatchedObligationsCard,
    type ObligationReviewEntry,
    type ObligationReviewState,
} from './MatchedObligationsCard';
import { countBreachedObligations, countObligationOverrides, hasUnresolvedAiBreach } from './ObligationVerdictList';
import { buildObligationReviewPayload, hydrateObligationReviews } from './obligation-review-state';
import { ResultHeader } from './ResultHeader';
import {
    ReviewDecisionRail,
    ReviewDecisionRailFooter,
    ReviewDecisionRailHeader,
    TeachOutcomeCaption,
} from './ReviewDecisionRail';
import {
    type FeedbackEntry,
    type FeedbackField,
    type FeedbackState,
    hasUnsavedFeedbackChanges,
    ReviewerDisagreementSection,
} from './ReviewerDisagreementSection';
import { ReviewWorkspace } from './ReviewWorkspace';
import { ReviewWorkspaceTitle } from './ReviewWorkspaceTitle';
import {
    canSkipClassification,
    canSubmitClassificationReview,
    resolveReviewHydration,
    reviewClassificationRefetchInterval,
} from './review-hydration';
import {
    consumeTeachOnSubmit,
    fanOutCombinedFeedback,
    hasUnsavedReviewState,
    isBreachOnlyDecision,
    resolveReviewSubmitDestination,
    resolveTeachPreviewOutcome,
    teachingNote,
} from './review-learning-state';
import { ShortcutsHelp } from './ShortcutsHelp';
import { SkipDialog } from './SkipDialog';
import { useReviewAiOriginalClassification } from './use-review-ai-original-classification';
import { useTeachSettleDeadline } from './use-teach-settle-deadline';
import { VulnerabilityCard } from './VulnerabilityCard';

export const Route = createFileRoute('/console/$orgId/tools/auto-detect/inbox/$id/review-email-classification')({
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: ReviewEmailClassification,
    staticData: {
        presentation: 'overlay',
        modules: ['auto-detect'],
        permissions: [PERMISSIONS.MESSAGE_CLASSIFICATION_ACCESS],
    },
});

type ReviewInput = RouterInputs['messageClassification']['reviewMessageClassification'];
type ReviewResult = RouterOutputs['messageClassification']['reviewMessageClassification'];
type SkipInput = RouterInputs['messageClassification']['skipMessageClassification'];
type FormValues = ReviewInput['set'];
type Data = RouterOutputs['messageClassification']['getMessageClassification'];

const FormItem = Form.Item<FormValues>;
const REVIEW_FORM_FIELD_NAMES = [
    'isIncident',
    'isComplaint',
    'confidence',
    'isBreachOfRegulation',
    'breachedRegulation',
    'justification',
    'tags',
] as const satisfies ReadonlyArray<keyof FormValues>;

const FEEDBACK_FIELDS = [
    'isIncident',
    'isComplaint',
    'confidence',
    'isBreachOfRegulation',
] as const satisfies ReadonlyArray<FeedbackField>;

type ActiveTab = 'context' | 'review' | 'debug';
/**
 * The queue move a submit branch wants once its teach flow is done. Handed to
 * `teachAndOfferRecheck` rather than decided by the caller: the teach can outrun
 * the settle deadline, and only the teach itself knows whether it ended by
 * opening a re-check offer (which owns the move) or without one (which must
 * perform it). A caller deciding at deadline time either carried the reviewer
 * away from an offer still coming, or stranded them on the reviewed message.
 */
type DeferredReviewAction = { kind: 'close' } | { kind: 'next'; nextId: bigint };
function ReviewEmailClassification() {
    const { id } = Route.useParams();
    const orgId = useOrgId();
    const user = useUser();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const enableDebugInfo = useFeatureFlag('enable-debug-info');
    const enableClassificationLearning = useFeatureFlag(ENABLE_CLASSIFICATION_LEARNING_FLAG, false);
    // v2 = the three-pane workspace (message | findings | decision rail). Presentation only: it
    // drives the same form, mutations, permissions and payload as v1, so there is no server-side
    // counterpart to gate — nothing new is reachable when the flag is on.
    const enableWorkspaceV2 = useFeatureFlag('enable-review-classification-v2', false);
    const [open, setOpen] = useState(true);
    const [activeTab, setActiveTab] = useState<ActiveTab>('context');
    const [skipOpen, setSkipOpen] = useState(false);
    // Messages submitted without leaving the drawer, for the queue strip's session progress.
    const [reviewedThisSession, setReviewedThisSession] = useState(0);
    // CUR-886 — "No issue" quick verdict: swaps the per-axis reason capture for
    // one combined reason (plan §7.1). Reset whenever a different record hydrates.
    const [quickNoIssue, setQuickNoIssue] = useState(false);
    // CUR-886 — pre-submit teach opt-in (decided on the message it applies to)
    // and the retroactive re-check modal. The queue advance waits for the teach
    // flow so the offer always opens over the message it was learned from.
    const [teachOnSubmit, setTeachOnSubmit] = useState(false);
    const [recheckTarget, setRecheckTarget] = useState<RecheckTarget | null>(null);
    // True while the post-submit teach + similarity preview is in flight. Keeps the
    // commit action busy and the pager frozen so the reviewer isn't left on an
    // apparently-idle screen that then jumps.
    const { isSettling: isTeachSettling, settle: settleTeach } = useTeachSettleDeadline();
    // Live route identity for the teach flow's anchor check. The teach closure
    // captures the record it was taught from, so it needs a value that tracks
    // where the reviewer actually is now, not where they were at submit time.
    const currentIdRef = useRef(id);
    currentIdRef.current = id;
    // Every accepted submission gets an identity synchronously, before its
    // mutation starts. A later submission supersedes the previous teach even
    // when it does not teach itself or while its save is still in flight.
    const reviewSubmissionGenerationRef = useRef(0);
    // An edit after an accepted submission only cancels that submission's held
    // queue move. It must not hide the learned-decision offer while the reviewer
    // remains on the message the offer belongs to.
    const reviewEditedAfterSubmitRef = useRef(false);
    useEffect(
        () => () => {
            reviewSubmissionGenerationRef.current += 1;
        },
        [],
    );

    const { data, isPending } = trpc.messageClassification.getMessageClassification.useQuery(
        { id },
        { refetchInterval: reviewClassificationRefetchInterval },
    );
    const isClassificationPending = data?.status === 'PENDING';
    const isClassificationFailed = data?.status === 'ERROR';
    const canSubmitReview = data ? canSubmitClassificationReview(data.status) : false;
    const reviewEditingEnabled = canSubmitReview && !isTeachSettling;
    const markDeferredReviewEdited = useCallback(() => {
        if (reviewEditingEnabled) reviewEditedAfterSubmitRef.current = true;
    }, [reviewEditingEnabled]);
    const invalidateDeferredTeach = useCallback(() => {
        reviewSubmissionGenerationRef.current += 1;
    }, []);
    const canSkip = data ? canSkipClassification(data.status) : false;
    const { mutate: updateMessageClassification, isPending: isSubmitting } =
        trpc.messageClassification.reviewMessageClassification.useMutation();
    const { mutate: skipMessageMutation, isPending: isSkipping } =
        trpc.messageClassification.skipMessageClassification.useMutation();
    const { mutateAsync: teachExample } = trpc.messageClassification.teachClassificationExample.useMutation();
    const { data: availableTags } = trpc.messageClassification.loadTags.useQuery();

    // Drawer navigation — pager pulls visible row IDs + the active
    // filter label from the inbox so we can render "X of Y in <filter>".
    const visibleIds = useInboxVisibleIds();
    const filterLabel = useInboxFilterLabel();
    const { currentIndex, prevId, nextId } = useMemo(() => {
        const idx = visibleIds.findIndex((vid) => vid === id);
        return {
            currentIndex: idx,
            prevId: idx > 0 ? visibleIds[idx - 1] : null,
            nextId: idx >= 0 && idx < visibleIds.length - 1 ? visibleIds[idx + 1] : null,
        };
    }, [visibleIds, id]);

    // Subject of the message Next would land on — the queue strip shows it so "Next" is a
    // decision (another breach? a newsletter?) rather than a leap.
    const nextSubject = useInboxSubject(nextId);

    function navigateToId(targetId: bigint) {
        navigate({
            to: '/console/$orgId/tools/auto-detect/inbox/$id/review-email-classification',
            params: { orgId, id: targetId },
        });
    }

    const [form] = Form.useForm<FormValues>();
    // Keyed by id + AI-output identity, not id alone: a rerun/reprocess that
    // completes while the drawer is open replaces the record's verdict under
    // us, and the refetch must rehydrate the form. Keying on the id only left
    // the form showing the PREVIOUS verdict while the AI panes showed the new
    // one — manufacturing "changes from the AI" the reviewer never made.
    // `processedAt`/`llmCorrelationId` change on every (re)classification but
    // not on review submits, so "Save and stay" keeps its feedback baseline.
    const hydratedReviewKeyRef = useRef<string | null>(null);
    const pendingNotificationIdRef = useRef<bigint | null>(null);
    // Set by "Save and stay" so the shared submit handler skips the advance-to-next step. A ref
    // rather than state: it is read once inside the mutation callback and must not re-render.
    const stayAfterSubmitRef = useRef(false);
    // Set when a teach flow ends in a re-check offer: the navigation it interrupted is handed to
    // the modal's onClose. Advancing (or closing the drawer) sooner would move the offer onto a
    // message it says nothing about — the confusion this defers around.
    const afterRecheckRef = useRef<{ kind: 'close' } | { kind: 'next'; nextId: bigint } | null>(null);
    const isBreachOfRegulation = Form.useWatch('isBreachOfRegulation', form);
    const watchedIsIncident = Form.useWatch('isIncident', form);
    const watchedIsComplaint = Form.useWatch('isComplaint', form);
    const watchedConfidence = Form.useWatch('confidence', form);
    // Live view of the drafted verdict for the breach-only teach exclusion —
    // undefined (pre-hydration) reads as false on every axis.
    const draftedBreachOnly = isBreachOnlyDecision({
        isIncident: watchedIsIncident === true,
        isComplaint: watchedIsComplaint === true,
        isBreachOfRegulation: isBreachOfRegulation === true,
    });
    const aiOriginalClassification = useReviewAiOriginalClassification(data);

    // Per-obligation reviews, claim link decisions, and AI-disagreement
    // feedback live outside the Ant Form (their shape doesn't fit
    // Form.Item ergonomics). State is hydrated from server-returned
    // data and reset whenever we switch messages.
    const [obligationReviews, setObligationReviews] = useState<ObligationReviewState>({});
    const [claimDecisions, setClaimDecisions] = useState<ClaimDecisionState>({});
    const [feedback, setFeedback] = useState<FeedbackState>({});
    const [savedFeedback, setSavedFeedback] = useState<FeedbackState>({});

    // Obligation reviews hydrate from data so referential equality alone
    // isn't dirty. Flip this on the first user-driven change and reset
    // on hydrate so the unsaved-changes guard catches per-obligation
    // edits that don't touch the main form.
    const [obligationReviewsDirty, setObligationReviewsDirty] = useState(false);

    // Track whether the reviewer pulled in the AI-rendered Justification
    // / Breached regulation text via the "Use AI reasoning" CTA. Any
    // subsequent edit flips the flag back to false so the audit log
    // can distinguish "reviewer signed off on AI prose" from
    // "reviewer wrote their own prose".
    const [justificationDerivedFromAi, setJustificationDerivedFromAi] = useState(false);
    const [breachDetailsDerivedFromAi, setBreachDetailsDerivedFromAi] = useState(false);

    useEffect(() => {
        if (!data) return;
        const hydration = resolveReviewHydration(hydratedReviewKeyRef.current, data);
        if (hydration.kind === 'pending' || hydration.kind === 'failed') {
            hydratedReviewKeyRef.current = hydration.key;
            if (!hydration.sameRecord) {
                // A queued/failed row opened through queue navigation has no
                // reviewable output. Do not leak the previous record's form
                // state into its read-only workspace.
                form.resetFields();
                setObligationReviews({});
                setObligationReviewsDirty(false);
                setClaimDecisions({});
                setFeedback({});
                setSavedFeedback({});
                setJustificationDerivedFromAi(false);
                setBreachDetailsDerivedFromAi(false);
                setQuickNoIssue(false);
                setTeachOnSubmit(false);
            }
            if (hydration.kind === 'pending' && hydration.sameRecord && pendingNotificationIdRef.current !== data.id) {
                message.info('This message is being re-classified — review submission is paused until it finishes.');
            }
            pendingNotificationIdRef.current = hydration.kind === 'pending' ? data.id : null;
            return;
        }
        pendingNotificationIdRef.current = null;
        if (hydration.kind === 'unchanged') return;

        // Same record, different AI output — the record was re-classified while
        // the drawer was open. Tell the reviewer why their in-progress state is
        // being replaced instead of silently swapping it.
        if (hydration.reclassified) {
            message.info('This message was re-classified — the review form has been refreshed with the new AI output.');
        }

        hydratedReviewKeyRef.current = hydration.key;

        // Hydrate the form. Note: justification and breachedRegulation
        // are intentionally NOT pre-populated from the AI's text — the
        // reviewer must explicitly opt in via "Use AI reasoning" so the
        // audit log isn't padded with rubber-stamped AI prose.
        const hasPersistedReviewerValues = hydration.hasPersistedReviewerValues;
        const nextValues = {
            isIncident: data.isIncident ?? false,
            isComplaint: data.isComplaint ?? false,
            confidence: data.confidence || 'LOW',
            isBreachOfRegulation: data.isBreachOfRegulation || false,
            // Show previously-reviewed prose verbatim, but on a fresh
            // record start empty (AI text is offered as a sidecar
            // alongside, not silently injected).
            breachedRegulation: hasPersistedReviewerValues ? data.breachedRegulation || '' : '',
            justification: hasPersistedReviewerValues ? data.justification || '' : '',
            tags: data.tags || [],
            justificationDerivedFromAi: false,
            breachDetailsDerivedFromAi: false,
        } satisfies Partial<FormValues>;

        form.setFieldsValue(nextValues);
        form.setFields(REVIEW_FORM_FIELD_NAMES.map((name) => ({ name, touched: false })));

        setObligationReviews(hydrateObligationReviews(data));
        setObligationReviewsDirty(false);
        setClaimDecisions({});
        setFeedback({});
        setSavedFeedback({});
        setJustificationDerivedFromAi(false);
        setBreachDetailsDerivedFromAi(false);
        setQuickNoIssue(false);
        setTeachOnSubmit(false);
    }, [data, form]);

    useEffect(() => {
        if (!canSkip) setSkipOpen(false);
    }, [canSkip]);

    // Build the disagreement diff: any field that differs from the AI's
    // pre-review value needs a reasonCode before we let the form submit.
    // The two classification axes are diffed INDEPENDENTLY — flipping both
    // produces two feedback rows, matching the FEEDBACK_FIELDS check
    // constraint on MessageClassificationFeedback.
    const differingFields = useMemo<FeedbackField[]>(() => {
        if (!aiOriginalClassification) return [];
        const diffs: FeedbackField[] = [];
        if (
            typeof watchedIsIncident === 'boolean' &&
            typeof aiOriginalClassification.isIncident === 'boolean' &&
            watchedIsIncident !== aiOriginalClassification.isIncident
        ) {
            diffs.push('isIncident');
        }
        if (
            typeof watchedIsComplaint === 'boolean' &&
            typeof aiOriginalClassification.isComplaint === 'boolean' &&
            watchedIsComplaint !== aiOriginalClassification.isComplaint
        ) {
            diffs.push('isComplaint');
        }
        if (
            watchedConfidence &&
            aiOriginalClassification.confidence &&
            watchedConfidence !== aiOriginalClassification.confidence
        ) {
            diffs.push('confidence');
        }
        if (
            typeof isBreachOfRegulation === 'boolean' &&
            typeof aiOriginalClassification.isBreachOfRegulation === 'boolean' &&
            isBreachOfRegulation !== aiOriginalClassification.isBreachOfRegulation
        ) {
            diffs.push('isBreachOfRegulation');
        }
        return diffs;
    }, [aiOriginalClassification, watchedIsIncident, watchedIsComplaint, watchedConfidence, isBreachOfRegulation]);

    // CUR-886 §7.1 — the quick action legitimately raises certainty to High, so
    // a certainty-only divergence must not demand a reason on the one-click
    // path. All other axes keep the reason requirement (served by the combined
    // panel when the quick action is active).
    const reasonRequiredFields = useMemo(
        () => (quickNoIssue ? differingFields.filter((field) => field !== 'confidence') : differingFields),
        [differingFields, quickNoIssue],
    );

    // Fan one combined reason out to every differing feedback field — the
    // per-field rows keep their own aiValue/reviewerValue, only the reason
    // (and optional note) is shared.
    const handleCombinedFeedbackChange = useCallback(
        (patch: Partial<FeedbackEntry>) => {
            if (!aiOriginalClassification || !reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setFeedback((prev) => {
                const values = Object.fromEntries(
                    FEEDBACK_FIELDS.map((field) => [
                        field,
                        {
                            aiValue: aiValueForField(aiOriginalClassification, field),
                            reviewerValue: reviewerValueForField(form, isBreachOfRegulation, field),
                        },
                    ]),
                ) as Record<FeedbackField, Pick<FeedbackEntry, 'aiValue' | 'reviewerValue'>>;
                return fanOutCombinedFeedback({ fields: differingFields, previous: prev, values, patch });
            });
        },
        [
            aiOriginalClassification,
            reviewEditingEnabled,
            markDeferredReviewEdited,
            form,
            isBreachOfRegulation,
            differingFields,
        ],
    );

    // The diff-set can grow AFTER the combined reason was chosen (the quick verdict or a manual
    // control edit flips another axis) — the new row would block submit while the panel still
    // renders the chosen chip. Re-fan the chosen reason out whenever the set changes.
    useEffect(() => {
        if (!quickNoIssue) return;
        // The panel displays the entry keyed off the first differing field — that IS the combined reason.
        const combined = differingFields[0] ? feedback[differingFields[0]] : undefined;
        if (!combined?.reasonCode) return;
        if (!reasonRequiredFields.some((field) => !feedback[field]?.reasonCode)) return;
        handleCombinedFeedbackChange({ reasonCode: combined.reasonCode, note: combined.note });
    }, [quickNoIssue, differingFields, feedback, reasonRequiredFields, handleCombinedFeedbackChange]);

    const attemptClose = useCallback(() => {
        // Closing navigates back to the inbox and unmounts this route, which would
        // drop the in-flight teach: its re-check offer would never render and its
        // deferred queue advance would fire from a dead component, dragging the
        // reviewer back into a drawer they just left. The settle deadline below
        // guarantees this temporary hold cannot become a keyboard trap.
        if (isTeachSettling) return;
        // Catch every shape of dirty state — form fields, obligation
        // toggle/note edits, claim CONFIRM/REJECT clicks, disagreement
        // chips/notes, and the "Use AI reasoning" buttons (which set
        // form values programmatically and don't trip Ant's touched
        // tracking on their own).
        //
        // Both `claimDecisions` and `feedback` keep stale entries after the
        // reviewer reverts a change (decision back to null, classification
        // back to the AI's value). Filter those out so the discard modal
        // only fires when something is actually unsaved.
        const hasActiveClaimDecision = Object.values(claimDecisions).some((entry) => entry.decision !== null);
        const hasUnsavedFeedback = hasUnsavedFeedbackChanges(differingFields, feedback, savedFeedback);
        const dirty = hasUnsavedReviewState({
            formTouched: form.isFieldsTouched(),
            obligationReviewsDirty,
            hasActiveClaimDecision,
            hasUnsavedFeedback,
            justificationDerivedFromAi,
            breachDetailsDerivedFromAi,
            teachOnSubmit,
        });
        if (dirty) {
            modal.confirm({
                title: 'Discard unsaved changes?',
                content: 'You have edits to this review that will be lost.',
                okText: 'Discard',
                okButtonProps: { danger: true },
                cancelText: 'Keep editing',
                onOk: () => setOpen(false),
            });
        } else {
            setOpen(false);
        }
    }, [
        form,
        isTeachSettling,
        obligationReviewsDirty,
        claimDecisions,
        feedback,
        savedFeedback,
        differingFields,
        justificationDerivedFromAi,
        breachDetailsDerivedFromAi,
        teachOnSubmit,
    ]);

    const handleObligationReviewChange = useCallback(
        (obligationId: string, patch: Partial<ObligationReviewEntry>) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setObligationReviewsDirty(true);
            setObligationReviews((prev) => ({
                ...prev,
                [obligationId]: {
                    relevant: prev[obligationId]?.relevant ?? true,
                    breach: prev[obligationId]?.breach ?? null,
                    note: prev[obligationId]?.note ?? '',
                    ...patch,
                },
            }));
        },
        [reviewEditingEnabled, markDeferredReviewEdited],
    );

    const handleClaimDecisionChange = useCallback(
        (claimId: string, source: MessageClassificationClaimLinkSource, decision: 'CONFIRM' | 'REJECT' | null) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setClaimDecisions((prev) => {
                return {
                    ...prev,
                    [claimId]: { source, decision },
                };
            });
        },
        [reviewEditingEnabled, markDeferredReviewEdited],
    );

    const handleFeedbackChange = useCallback(
        (field: FeedbackField, patch: Partial<FeedbackEntry>) => {
            if (!aiOriginalClassification || !reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setFeedback((prev) => {
                const aiValue = aiValueForField(aiOriginalClassification, field);
                const reviewerValue = reviewerValueForField(form, isBreachOfRegulation, field);
                return {
                    ...prev,
                    [field]: {
                        aiValue,
                        reviewerValue,
                        reasonCode: prev[field]?.reasonCode ?? null,
                        note: prev[field]?.note ?? '',
                        ...patch,
                    },
                };
            });
        },
        [aiOriginalClassification, reviewEditingEnabled, markDeferredReviewEdited, form, isBreachOfRegulation],
    );

    // Keep the cached AI/reviewer values fresh as the form changes —
    // without this, the disagreement card shows stale values when the
    // reviewer toggles back and forth.
    useEffect(() => {
        if (!aiOriginalClassification) return;
        setFeedback((prev) => {
            const next: FeedbackState = { ...prev };
            for (const field of FEEDBACK_FIELDS) {
                const aiValue = aiValueForField(aiOriginalClassification, field);
                const reviewerValue = reviewerValueForField(form, isBreachOfRegulation, field);
                const existing = next[field];
                if (existing) {
                    next[field] = { ...existing, aiValue, reviewerValue };
                }
            }
            return next;
        });
    }, [
        aiOriginalClassification,
        watchedIsIncident,
        watchedIsComplaint,
        watchedConfidence,
        isBreachOfRegulation,
        form,
    ]);

    // CUR-886 — teach flow shared by every submit branch: persist the learned example, then
    // offer the retroactive re-check when similar unreviewed messages exist. Resolves true when
    // the re-check modal opened. Await-based rather than mutate-scoped callbacks: every branch
    // holds its navigation until this settles, because navigating away drops mutate-scoped
    // callbacks — a failed teach (or its re-check offer) would vanish silently.
    async function teachAndOfferRecheck(
        record: Data,
        feedbackPayload: ReviewInput['feedback'],
        reviewToken: ReviewResult['reviewToken'],
        deferredAction: DeferredReviewAction | null,
        submissionGeneration: number,
    ): Promise<void> {
        // Offers belong to the submission and message that created them. Edits
        // do not make that offer stale; they only make its held queue move stale.
        const isOfferStale = (): boolean =>
            reviewSubmissionGenerationRef.current !== submissionGeneration || currentIdRef.current !== record.id;
        // Hand the caller's queue move to whichever way the teach ends, exactly
        // once: an offer takes ownership of the move, no offer performs it now.
        let deferredSettled = false;
        const settleDeferred = (offerOpened: boolean): void => {
            if (deferredSettled || deferredAction === null) return;
            deferredSettled = true;
            if (isOfferStale() || reviewEditedAfterSubmitRef.current) return;
            if (offerOpened) {
                afterRecheckRef.current = deferredAction;
            } else if (deferredAction.kind === 'next') {
                navigateToId(deferredAction.nextId);
            } else {
                setOpen(false);
            }
        };

        if (!enableClassificationLearning) {
            settleDeferred(false);
            return;
        }
        const noun = record.messageType === 'SMS' ? 'messages' : 'emails';
        const sourceHeadline = record.subject || 'the reviewed message';
        message.loading(`Saving the learned decision and looking for similar ${noun}...`);

        const settled = await settleTeach(async () => {
            try {
                const result = await teachExample({
                    messageClassificationId: record.id,
                    reviewToken,
                    reasonCode: feedbackPayload?.[0]?.reasonCode,
                    note: teachingNote(feedbackPayload ?? []),
                });
                void trpcUtils.messageClassification.listLearnedExamples.invalidate();
                void trpcUtils.messageClassification.listLearnedExampleManagementMetadata.invalidate();
                if (isOfferStale()) return false;

                try {
                    // Fetch through the query cache so the RecheckSimilarModal's own
                    // useQuery reads this result instead of repeating the billed
                    // embedding + vector search on mount (its staleTime covers the gap).
                    const preview = await trpcUtils.messageClassification.previewSimilarToExample.fetch({
                        id: result.id,
                    });
                    // A deadline may have released the reviewer onto the next
                    // message while this unabortable query was still resolving.
                    // Never let its late offer attach to that new message.
                    if (isOfferStale()) return false;

                    const previewOutcome = resolveTeachPreviewOutcome(preview);
                    if (previewOutcome === 'unavailable') {
                        message.warning(
                            'Learned decision saved, but similar-message discovery is unavailable. Retry here or later from Learned decisions.',
                        );
                        setRecheckTarget({ exampleId: result.id, channel: record.messageType, sourceHeadline });
                        return true;
                    }
                    if (previewOutcome === 'matches') {
                        setRecheckTarget({ exampleId: result.id, channel: record.messageType, sourceHeadline });
                        return true;
                    }
                    message.success(`Learned decision saved — no similar unreviewed ${noun} to re-check.`);
                } catch {
                    if (isOfferStale()) return false;
                    message.warning(
                        'Learned decision saved, but similar-message discovery failed. Retry from Learned decisions.',
                    );
                    setRecheckTarget({ exampleId: result.id, channel: record.messageType, sourceHeadline });
                    return true;
                }
            } catch (error) {
                if (!isOfferStale()) errorMessage.show(error);
            }
            return false;
        }, false);

        // Cancelled means the reviewer left or a newer teach took over; whoever
        // did that owns the queue now, so this run's move is moot.
        if (settled.cancelled) return;
        if (settled.timedOut) {
            // The hold is released, not the work. Wait for how it actually ends
            // before moving the reviewer: an offer takes the queue move, no
            // offer performs it. Dropping the move here is what stranded
            // Submit-and-next whenever a slow teach found nothing to re-check.
            message.warning(
                'Learning is taking longer than expected — you have been kept on this message. Any re-check offer will open here; you can also find the outcome later in Learned decisions.',
            );
            void settled.completion.then((offerOpened) => {
                settleDeferred(offerOpened);
            });
            return;
        }
        settleDeferred(settled.value);
    }

    // After a successful submit the committed values become the clean baseline. Used by "Save and
    // stay" and by the last-item branch, which keeps the drawer open while the teach flow settles.
    function markReviewSaved() {
        form.setFields(REVIEW_FORM_FIELD_NAMES.map((name) => ({ name, touched: false })));
        setObligationReviewsDirty(false);
        setClaimDecisions({});
        // The saved overrides still differ from the AI, so retain their reasons
        // for subsequent edits and make them the new clean baseline.
        setSavedFeedback(feedback);
        setJustificationDerivedFromAi(false);
        setBreachDetailsDerivedFromAi(false);
    }

    async function handleSubmit(values: FormValues) {
        // Consume the "stay put" intent up front. Every early return below (and antd's own
        // validation, which never reaches this function) would otherwise leave the ref set, so a
        // later Submit-and-next would silently decline to advance.
        const stayAfterSubmit = stayAfterSubmitRef.current;
        stayAfterSubmitRef.current = false;

        if (!data || !canSubmitClassificationReview(data.status) || isTeachSettling) return;

        // Block submit if any differing field is missing a reasonCode.
        const missingReason = reasonRequiredFields.find((field) => !feedback[field]?.reasonCode);
        if (missingReason) {
            // v1 hides the reason capture behind a tab, so the reviewer has to be sent there.
            // v2 renders it inline in the always-visible rail, so the toast alone is enough.
            if (!enableWorkspaceV2) setActiveTab('review');
            message.error('Pick a reason for each value you changed from the AI.');
            return;
        }

        // Allocate this before dispatching the mutation. A second accepted
        // submit invalidates any older timed-out teach immediately, including
        // when the new submission does not opt into learning or later fails.
        const submissionGeneration = ++reviewSubmissionGenerationRef.current;
        reviewEditedAfterSubmitRef.current = false;

        const obligationReviewPayload = buildObligationReviewPayload(data.matchedObligations, obligationReviews);

        const linkedClaimPayload = buildLinkedClaimDecisionPayload(data, claimDecisions);

        const feedbackPayload = differingFields
            .map((field) => {
                const entry = feedback[field];
                if (!entry?.reasonCode) return null;
                return {
                    field,
                    aiValue: entry.aiValue,
                    reviewerValue: entry.reviewerValue,
                    reasonCode: entry.reasonCode,
                    note: entry.note || undefined,
                };
            })
            .filter((row): row is NonNullable<typeof row> => row !== null);

        message.loading('Reviewing classification...');
        updateMessageClassification(
            {
                id: data.id,
                expectedOutput: {
                    llmCorrelationId: data.llmCorrelationId,
                    processedAt: data.processedAt,
                },
                set: {
                    isIncident: values.isIncident,
                    isComplaint: values.isComplaint,
                    confidence: values.confidence,
                    isBreachOfRegulation: values.isBreachOfRegulation,
                    breachedRegulation: values.breachedRegulation,
                    justification: values.justification,
                    tags: values.tags,
                    justificationDerivedFromAi,
                    breachDetailsDerivedFromAi,
                },
                obligationReviews: obligationReviewPayload,
                linkedClaimDecisions: linkedClaimPayload,
                feedback: feedbackPayload,
            },
            {
                onSuccess(reviewResult) {
                    message.success('Classification reviewed successfully.');
                    trpcUtils.messageClassification.invalidate();
                    setReviewedThisSession((count) => count + 1);
                    // CUR-886 — the reviewer opted in on the rail's "Learn from
                    // this decision" BEFORE submitting, so teaching runs now,
                    // attributed to the message it belongs to. The re-check offer
                    // only opens when matches exist (it names its source, since
                    // the queue may already have advanced); zero matches is a
                    // toast, never a modal with a disabled "Re-check 0" button.
                    const teaching = consumeTeachOnSubmit({
                        teachOnSubmit: enableClassificationLearning && teachOnSubmit,
                        messageType: data.messageType,
                        // The token carries the adjudicated axes the server just
                        // committed — the authoritative values for the
                        // breach-only exclusion, not the pre-review record.
                        decision: reviewResult.reviewToken,
                    });
                    const shouldTeach = teaching.shouldTeach;
                    // Learning is a one-shot opt-in. Consume it before any async teach branch so
                    // Save and stay cannot teach the same row again on a later edit/save.
                    if (teachOnSubmit !== teaching.nextTeachOnSubmit) {
                        setTeachOnSubmit(teaching.nextTeachOnSubmit);
                    }
                    // "Save and stay" keeps the reviewer on the message they just committed —
                    // the escape hatch for when they want to re-read it or open the record they
                    // just created, instead of being carried to the next item.
                    const destination = resolveReviewSubmitDestination({
                        stayAfterSubmit,
                        nextId: nextId ?? undefined,
                        shouldTeach,
                    });
                    if (destination.kind === 'stay') {
                        if (shouldTeach)
                            void teachAndOfferRecheck(
                                data,
                                feedbackPayload,
                                reviewResult.reviewToken,
                                null,
                                submissionGeneration,
                            );
                        markReviewSaved();
                        return;
                    }
                    if (destination.kind === 'next') {
                        if (!shouldTeach) {
                            navigateToId(destination.nextId);
                            return;
                        }
                        // Teaching holds the queue advance. Advancing first put the re-check
                        // offer over the NEXT message, where it reads as a question about that
                        // message rather than the one just reviewed. The saved baseline stops an
                        // interim Escape raising a false discard warning while it settles.
                        markReviewSaved();
                        void teachAndOfferRecheck(
                            data,
                            feedbackPayload,
                            reviewResult.reviewToken,
                            {
                                kind: 'next',
                                nextId: destination.nextId,
                            },
                            submissionGeneration,
                        );
                        return;
                    }
                    // Last message in the queue: the drawer must not close until the teach flow
                    // settles — closing navigates away and unmounts the route, silently dropping
                    // the teach outcome. A re-check offer takes over the close via its onClose.
                    if (destination.kind === 'last-item-teach') {
                        markReviewSaved();
                        void teachAndOfferRecheck(
                            data,
                            feedbackPayload,
                            reviewResult.reviewToken,
                            { kind: 'close' },
                            submissionGeneration,
                        );
                        return;
                    }
                    setOpen(false);
                },
                onError(error) {
                    // PRECONDITION means the hydrated output was superseded;
                    // refreshing this exact detail query gives the drawer the
                    // replacement identity and hydration decision immediately.
                    void trpcUtils.messageClassification.getMessageClassification.invalidate({ id: data.id });
                    errorMessage.show(error);
                },
            },
        );
    }

    function handleSkipSubmit(values: Pick<SkipInput, 'skipCategory' | 'note'>) {
        if (!data || !canSkip || isTeachSettling) return;
        // Opening the dialog already invalidates a late offer; repeat this at
        // the mutation boundary so submit stays safe if another entry path is added.
        invalidateDeferredTeach();
        message.loading('Skipping message...');
        skipMessageMutation(
            {
                id: data.id,
                skipCategory: values.skipCategory,
                note: values.note,
            },
            {
                onSuccess() {
                    message.success('Message marked as not relevant.');
                    trpcUtils.messageClassification.invalidate();
                    setSkipOpen(false);
                    if (nextId) {
                        navigateToId(nextId);
                    } else {
                        setOpen(false);
                    }
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    }

    // Keyboard shortcuts — listed in ShortcutsHelp. Disabled in
    // textarea/input focus so typing isn't intercepted, and while the
    // re-check modal is open — useHotkey listens on window and antd's
    // modal only intercepts Esc, so without the gate keystrokes would
    // act on (or even submit) the message UNDER the modal. Handlers are
    // wrapped in useCallback so the useHotkey effect doesn't re-attach
    // ~13 keydown listeners on every render.
    const isEditableFocus = useEditableFocusDetector();
    const drawerHotkeysEnabled = open && !skipOpen && recheckTarget === null && !isTeachSettling && !isEditableFocus;

    const submitForm = useCallback(() => form.submit(), [form]);
    const goNext = useCallback(() => {
        if (nextId) navigateToId(nextId);
        // navigateToId is stable enough — depends only on orgId from useOrgId
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextId]);
    const goPrev = useCallback(() => {
        if (prevId) navigateToId(prevId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevId]);
    const agreeWithAi = useCallback(() => {
        if (!data || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({
            isIncident: aiOriginalClassification?.isIncident ?? data.isIncident ?? false,
            isComplaint: aiOriginalClassification?.isComplaint ?? data.isComplaint ?? false,
            confidence: aiOriginalClassification?.confidence ?? data.confidence ?? 'LOW',
            isBreachOfRegulation: aiOriginalClassification?.isBreachOfRegulation ?? data.isBreachOfRegulation ?? false,
        });
        // v1 has to move the reviewer to the tab holding the controls it just reset; in v2 those
        // controls are already on screen in the rail.
        if (!enableWorkspaceV2) setActiveTab('review');
    }, [data, reviewEditingEnabled, markDeferredReviewEdited, form, aiOriginalClassification, enableWorkspaceV2]);
    const openSkipDialog = useCallback(() => {
        // Guarded here rather than per call site — the hotkey, the v1 footer and
        // the v2 rail all land on this. Skipping mid-teach would navigate away
        // from the message whose learned decision is still being saved.
        if (!canSkip || isTeachSettling) return;
        invalidateDeferredTeach();
        setSkipOpen(true);
    }, [canSkip, isTeachSettling, invalidateDeferredTeach]);
    const breachedObligationCount = useMemo(
        () => (data ? countBreachedObligations(data.matchedObligations, obligationReviews) : 0),
        [data, obligationReviews],
    );
    const obligationOverrideCount = useMemo(
        () => (data ? countObligationOverrides(data.matchedObligations, obligationReviews) : 0),
        [data, obligationReviews],
    );
    // CUR-886 — "No issue" quick verdict: pre-fills the negative verdict at
    // high certainty. When the AI itself found nothing (a certainty-only
    // confirmation), submit immediately — the true one-click path. Otherwise
    // the combined reason panel opens and the reviewer submits normally.
    const applyNoIssueQuickVerdict = useCallback(() => {
        if (!data || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFields([
            { name: 'isIncident', value: false, touched: true },
            { name: 'isComplaint', value: false, touched: true },
            { name: 'isBreachOfRegulation', value: false, touched: true },
            { name: 'confidence', value: 'HIGH', touched: true },
        ]);
        setQuickNoIssue(true);
        const aiWasNegative =
            (aiOriginalClassification?.isIncident ?? data.isIncident ?? false) === false &&
            (aiOriginalClassification?.isComplaint ?? data.isComplaint ?? false) === false &&
            (aiOriginalClassification?.isBreachOfRegulation ?? data.isBreachOfRegulation ?? false) === false;
        // A breached obligation verdict (or unsaved rail edits) contradicts
        // the negative verdict being submitted — hold the one-click submit so
        // the rail's contradiction warning can render and the reviewer
        // resolves it before submitting normally.
        if (aiWasNegative && breachedObligationCount === 0 && !obligationReviewsDirty) {
            form.submit();
        }
    }, [
        data,
        reviewEditingEnabled,
        markDeferredReviewEdited,
        form,
        aiOriginalClassification,
        breachedObligationCount,
        obligationReviewsDirty,
    ]);
    const toggleBreach = useCallback(() => {
        if (!reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({ isBreachOfRegulation: !isBreachOfRegulation });
    }, [reviewEditingEnabled, markDeferredReviewEdited, form, isBreachOfRegulation]);
    const goContextTab = useCallback(() => setActiveTab('context'), []);
    const goReviewTab = useCallback(() => setActiveTab('review'), []);
    const goDebugTab = useCallback(() => {
        if (enableDebugInfo) setActiveTab('debug');
    }, [enableDebugInfo]);

    // These three carry their own conditions rather than `drawerHotkeysEnabled`
    // (submit stays live while a field has focus, Escape must close from anywhere),
    // so they each need the settle gate spelled out: a second submit or an Escape
    // mid-teach would re-enter the flow or unmount the route under it.
    const commitHotkeysEnabled = open && !skipOpen && recheckTarget === null && !isTeachSettling;
    useHotkey('Meta+Enter', submitForm, { enabled: commitHotkeysEnabled && reviewEditingEnabled });
    useHotkey('Ctrl+Enter', submitForm, { enabled: commitHotkeysEnabled && reviewEditingEnabled });
    useHotkey('Escape', attemptClose, { enabled: commitHotkeysEnabled });
    useHotkey('j', goNext, { enabled: drawerHotkeysEnabled });
    useHotkey('k', goPrev, { enabled: drawerHotkeysEnabled });
    useHotkey('ArrowRight', goNext, { enabled: drawerHotkeysEnabled });
    useHotkey('ArrowLeft', goPrev, { enabled: drawerHotkeysEnabled });
    useHotkey('y', agreeWithAi, { enabled: drawerHotkeysEnabled });
    useHotkey('n', openSkipDialog, { enabled: drawerHotkeysEnabled && canSkip });
    useHotkey('b', toggleBreach, { enabled: drawerHotkeysEnabled });
    // The quick verdict's affordances (button, caption, reason capture) all
    // live in ReviewDecisionRail, which only renders under v2 — without this
    // gate a single keystroke could submit an adjudicated verdict in the v1
    // drawer with nothing on screen explaining it.
    useHotkey('0', applyNoIssueQuickVerdict, {
        enabled: drawerHotkeysEnabled && reviewEditingEnabled && enableWorkspaceV2,
    });
    // Tab-switching shortcuts only mean something in v1 — v2 has no tabs to switch to.
    useHotkey('1', goContextTab, { enabled: drawerHotkeysEnabled && !enableWorkspaceV2 });
    useHotkey('2', goReviewTab, { enabled: drawerHotkeysEnabled && !enableWorkspaceV2 });
    useHotkey('3', goDebugTab, { enabled: drawerHotkeysEnabled && enableDebugInfo && !enableWorkspaceV2 });

    const aiJustification = aiOriginalClassification?.justification ?? '';
    const aiBreachText = aiOriginalClassification?.breachedRegulation ?? '';

    const useAiJustification = useCallback(() => {
        if (!aiJustification || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({ justification: aiJustification });
        setJustificationDerivedFromAi(true);
    }, [aiJustification, reviewEditingEnabled, markDeferredReviewEdited, form]);

    const useAiBreachText = useCallback(() => {
        if (!aiBreachText || !reviewEditingEnabled) return;
        markDeferredReviewEdited();
        form.setFieldsValue({ breachedRegulation: aiBreachText });
        setBreachDetailsDerivedFromAi(true);
    }, [aiBreachText, reviewEditingEnabled, markDeferredReviewEdited, form]);

    const markJustificationEdited = useCallback(() => {
        if (reviewEditingEnabled) setJustificationDerivedFromAi(false);
    }, [reviewEditingEnabled]);

    const markBreachDetailsEdited = useCallback(() => {
        if (reviewEditingEnabled) setBreachDetailsDerivedFromAi(false);
    }, [reviewEditingEnabled]);

    const handleTeachOnSubmitChange = useCallback(
        (checked: boolean) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            setTeachOnSubmit(checked);
        },
        [reviewEditingEnabled, markDeferredReviewEdited],
    );

    const obligationsCount = data?.matchedObligations.length ?? 0;
    const formDirty = differingFields.length > 0;

    // Submit through the same handler, but hold position instead of advancing.
    const saveAndStay = useCallback(() => {
        if (!reviewEditingEnabled) return;
        stayAfterSubmitRef.current = true;
        form.submit();
    }, [reviewEditingEnabled, form]);

    // Reconciles the global breach flag with the per-obligation verdicts from the rail's warning.
    // `setFields` (not `setFieldsValue`) so the field is marked touched and the unsaved-changes
    // guard sees it — the reviewer did make this decision, they just made it in one click.
    const setBreachFlag = useCallback(
        (value: boolean) => {
            if (!reviewEditingEnabled) return;
            markDeferredReviewEdited();
            form.setFields([{ name: 'isBreachOfRegulation', value, touched: true }]);
        },
        [reviewEditingEnabled, markDeferredReviewEdited, form],
    );

    const actionableObligationCount = useMemo(
        () => data?.matchedObligations.filter((match) => Boolean(match.obligation?.id)).length ?? 0,
        [data],
    );
    const unresolvedAiBreach = useMemo(() => (data ? hasUnresolvedAiBreach(data.matchedObligations) : false), [data]);
    const classificationStatusNotice = isClassificationPending ? (
        <Alert
            type="info"
            showIcon
            title="Classification in progress"
            description="The review form will refresh when the new AI output is ready."
        />
    ) : isClassificationFailed && data ? (
        <Alert
            type="error"
            showIcon
            title="Classification failed"
            description={data.error || 'The classifier did not return a usable output.'}
        />
    ) : null;

    return (
        <PageController route={Route}>
            <Drawer
                title={
                    enableWorkspaceV2 && data ? (
                        <ReviewWorkspaceTitle data={data} classification={aiOriginalClassification} />
                    ) : (
                        'Review classification'
                    )
                }
                open={open}
                onClose={attemptClose}
                maskClosable={false}
                closable={!isTeachSettling}
                afterOpenChange={(opened) => {
                    if (!opened) {
                        navigate({
                            to: '/console/$orgId/tools/auto-detect/inbox',
                            params: { orgId },
                        });
                    }
                }}
                size="80vw"
                classNames={{ body: 'bg-background !p-0' }}
                // v2 puts the commit actions in the rail's pinned footer, beside the controls they
                // apply to, so the drawer footer would only add a second competing action bar.
                footer={
                    enableWorkspaceV2 ? null : (
                        <div className="flex w-full flex-wrap items-center justify-between gap-3">
                            <Space>
                                <Button
                                    type="tertiary"
                                    icon={<ChevronLeft />}
                                    disabled={!prevId || isTeachSettling}
                                    onClick={() => prevId && navigateToId(prevId)}
                                >
                                    Previous
                                </Button>
                                {currentIndex >= 0 && visibleIds.length > 0 ? (
                                    <span className="text-muted-foreground text-sm">
                                        {currentIndex + 1} of {visibleIds.length}
                                        {filterLabel ? ` in ${filterLabel}` : ''}
                                    </span>
                                ) : null}
                                <Button
                                    type="tertiary"
                                    disabled={!nextId || isTeachSettling}
                                    onClick={() => nextId && navigateToId(nextId)}
                                    icon={<ChevronRight />}
                                    iconPosition="end"
                                >
                                    Next
                                </Button>
                                <ShortcutsHelp />
                            </Space>
                            <Space>
                                <Button
                                    type="tertiary"
                                    onClick={openSkipDialog}
                                    loading={isSkipping}
                                    disabled={!canSkip || isTeachSettling}
                                >
                                    Mark as not relevant
                                </Button>
                                <Button type="link" onClick={attemptClose} disabled={isTeachSettling}>
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    loading={isSubmitting || isTeachSettling}
                                    disabled={!reviewEditingEnabled}
                                    htmlType="submit"
                                    form={`review-classification-${id}`}
                                >
                                    Submit review
                                </Button>
                            </Space>
                        </div>
                    )
                }
            >
                {isPending ? (
                    <div className="p-6">
                        <Skeleton active paragraph={{ rows: 8 }} />
                    </div>
                ) : data && enableWorkspaceV2 ? (
                    <Form<FormValues>
                        form={form}
                        id={`review-classification-${id}`}
                        layout="vertical"
                        onFinish={handleSubmit}
                        onValuesChange={markDeferredReviewEdited}
                        disabled={!reviewEditingEnabled}
                        className="h-full min-h-0"
                    >
                        <ReviewWorkspace
                            statusNotice={classificationStatusNotice}
                            reviewDisabled={!reviewEditingEnabled}
                            recordLinksDisabled={isTeachSettling}
                            orgId={orgId}
                            data={data}
                            userRoles={user.roles}
                            currentIndex={currentIndex}
                            totalInQueue={visibleIds.length}
                            filterLabel={filterLabel}
                            reviewedThisSession={reviewedThisSession}
                            nextSubject={nextSubject}
                            onPrevious={prevId && !isTeachSettling ? () => navigateToId(prevId) : undefined}
                            onNext={nextId && !isTeachSettling ? () => navigateToId(nextId) : undefined}
                            obligationReviews={obligationReviews}
                            onChangeObligationReview={handleObligationReviewChange}
                            claimDecisions={claimDecisions}
                            onChangeClaimDecision={handleClaimDecisionChange}
                            currentIsIncident={watchedIsIncident ?? data.isIncident}
                            currentIsComplaint={watchedIsComplaint ?? data.isComplaint}
                            currentIsBreachOfRegulation={isBreachOfRegulation ?? data.isBreachOfRegulation}
                            currentConfidence={watchedConfidence ?? data.confidence}
                            showDebugInfo={enableDebugInfo}
                            breachedObligationCount={breachedObligationCount}
                            aiJustification={aiJustification}
                            railHeader={
                                <ReviewDecisionRailHeader
                                    changeCount={differingFields.length + obligationOverrideCount}
                                />
                            }
                            railBody={
                                <ReviewDecisionRail
                                    aiOriginal={aiOriginalClassification}
                                    isBreachOfRegulation={isBreachOfRegulation}
                                    differingFields={differingFields}
                                    feedback={feedback}
                                    onFeedbackChange={handleFeedbackChange}
                                    breachedObligationCount={breachedObligationCount}
                                    actionableObligationCount={actionableObligationCount}
                                    hasUnresolvedAiBreach={unresolvedAiBreach}
                                    availableTags={availableTags ?? []}
                                    aiJustification={aiJustification}
                                    aiBreachText={aiBreachText}
                                    reviewerEditedBreachText={Boolean(data.reviewerEditedBreachText)}
                                    onUseAiJustification={useAiJustification}
                                    onUseAiBreachText={useAiBreachText}
                                    onJustificationEdited={markJustificationEdited}
                                    onBreachTextEdited={markBreachDetailsEdited}
                                    onSetBreach={setBreachFlag}
                                    onQuickNoIssue={applyNoIssueQuickVerdict}
                                    quickNoIssueDisabled={!reviewEditingEnabled}
                                    quickNoIssueActive={quickNoIssue}
                                    onCombinedFeedbackChange={handleCombinedFeedbackChange}
                                    teachOnSubmit={enableClassificationLearning && teachOnSubmit}
                                    onTeachOnSubmitChange={
                                        enableClassificationLearning ? handleTeachOnSubmitChange : undefined
                                    }
                                    teachDisabled={
                                        !reviewEditingEnabled || data.messageType === 'INTAKE' || draftedBreachOnly
                                    }
                                    teachDisabledReason={
                                        draftedBreachOnly
                                            ? 'Breach-only decisions cannot teach auto-detect — an affirmed breach is never learned, so this decision has nothing to teach.'
                                            : undefined
                                    }
                                    teachCaption={
                                        <TeachOutcomeCaption
                                            watched={{
                                                isIncident: watchedIsIncident,
                                                isComplaint: watchedIsComplaint,
                                                isBreachOfRegulation,
                                            }}
                                            persisted={{
                                                isIncident: data.isIncident,
                                                isComplaint: data.isComplaint,
                                                isBreachOfRegulation: data.isBreachOfRegulation,
                                            }}
                                        />
                                    }
                                />
                            }
                            railFooter={
                                <ReviewDecisionRailFooter
                                    formId={`review-classification-${id}`}
                                    differingFields={reasonRequiredFields}
                                    feedback={feedback}
                                    onSkip={openSkipDialog}
                                    onSaveAndStay={saveAndStay}
                                    isSubmitting={isSubmitting || isTeachSettling}
                                    isSkipping={isSkipping}
                                    hasNextMessage={Boolean(nextId)}
                                    hasObligationOverrides={obligationOverrideCount > 0}
                                    reviewDisabled={!reviewEditingEnabled}
                                    skipDisabled={!canSkip || isTeachSettling}
                                />
                            }
                        />
                    </Form>
                ) : data ? (
                    <div
                        className={`grid h-full min-h-0 grid-cols-1 lg:grid-cols-2 ${
                            classificationStatusNotice ? 'grid-rows-[auto_minmax(0,1fr)]' : 'grid-rows-[minmax(0,1fr)]'
                        }`}
                    >
                        {classificationStatusNotice ? (
                            <div className="col-span-full px-4 pt-4">{classificationStatusNotice}</div>
                        ) : null}
                        {/* Left Column: Email Content */}
                        <EmailReferencePanel data={data} orgId={orgId} />

                        {/* Right Column: AI result + reviewer override (tabbed) */}
                        <div className="flex min-h-0 flex-col">
                            <div className="border-separator border-b bg-muted px-6 py-4">
                                <ResultHeader data={data} classification={aiOriginalClassification} />
                            </div>
                            {data.SimilarTo &&
                                (data.skipCategory === 'DUPLICATE' || data.skipCategory === 'SEMANTIC_DUPLICATE') && (
                                    <div className="mx-6 mt-4 flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950/30">
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/50">
                                            <Copy size={16} className="text-orange-600 dark:text-orange-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-orange-800 text-xs dark:text-orange-300">
                                                {data.skipCategory === 'DUPLICATE'
                                                    ? 'Hash duplicate — identical content to a previously processed email'
                                                    : 'Semantic duplicate — similar meaning to a previously processed email'}
                                            </p>
                                            <p className="truncate text-orange-700 text-sm dark:text-orange-400">
                                                Duplicate of{' '}
                                                <InlineLink
                                                    to="/console/$orgId/tools/auto-detect/inbox/$id/review-email-classification"
                                                    params={{ orgId, id: data.SimilarTo.id }}
                                                    className="font-medium text-orange-700 decoration-orange-700/40 hover:text-orange-600 hover:decoration-orange-600 dark:text-orange-400 dark:decoration-orange-400/40 dark:hover:text-orange-300 dark:hover:decoration-orange-300"
                                                >
                                                    {data.SimilarTo.subject || `#${data.SimilarTo.id}`}
                                                </InlineLink>
                                            </p>
                                        </div>
                                    </div>
                                )}
                            <Tabs
                                activeKey={activeTab}
                                onChange={(key) => setActiveTab(key as ActiveTab)}
                                className="flex-1 [&_.ant-tabs-body-holder]:flex-1 [&_.ant-tabs-body-holder]:overflow-auto"
                                tabBarStyle={{ paddingLeft: 24, paddingRight: 24, marginBottom: 0 }}
                                items={[
                                    {
                                        key: 'context',
                                        label: (
                                            <span className="flex items-center gap-2">
                                                AI context
                                                {obligationsCount > 0 ? (
                                                    <Tag color="blue" className="!mr-0">
                                                        {obligationsCount}
                                                    </Tag>
                                                ) : null}
                                            </span>
                                        ),
                                        children: (
                                            <div className="px-6 py-4">
                                                <div className="flex flex-col gap-3">
                                                    <AiSummaryCard data={data} />
                                                    <MatchedObligationsCard
                                                        orgId={orgId}
                                                        matchedObligations={data.matchedObligations}
                                                        reviews={obligationReviews}
                                                        onChangeReview={handleObligationReviewChange}
                                                        disabled={!reviewEditingEnabled}
                                                    />
                                                    <VulnerabilityCard
                                                        orgId={orgId}
                                                        messageClassificationId={id}
                                                        matchedContacts={data.matchedContacts}
                                                        userRoles={user.roles}
                                                    />
                                                    <LinkedClaimsCard
                                                        orgId={orgId}
                                                        extractedClaimIds={data.extractedClaimIds}
                                                        matchedClaims={data.matchedClaims}
                                                        inferredFromSenderClaims={data.inferredFromSenderClaims}
                                                        linkedClaimDecisions={data.linkedClaimDecisions}
                                                        localDecisions={claimDecisions}
                                                        onChangeDecision={handleClaimDecisionChange}
                                                        disabled={!reviewEditingEnabled}
                                                    />
                                                    <ClassificationContextCards
                                                        orgId={orgId}
                                                        data={data}
                                                        currentIsIncident={watchedIsIncident ?? data.isIncident}
                                                        currentIsComplaint={watchedIsComplaint ?? data.isComplaint}
                                                        currentIsBreachOfRegulation={
                                                            isBreachOfRegulation ?? data.isBreachOfRegulation
                                                        }
                                                        currentConfidence={watchedConfidence ?? data.confidence}
                                                        recordLinksDisabled={isTeachSettling}
                                                    />
                                                    <ContactMatchesCard
                                                        orgId={orgId}
                                                        matchedContacts={data.matchedContacts}
                                                        recordLinksDisabled={isTeachSettling}
                                                    />
                                                </div>
                                            </div>
                                        ),
                                    },
                                    {
                                        key: 'review',
                                        label: (
                                            <span className="flex items-center gap-2">
                                                Review
                                                {formDirty ? (
                                                    <span className="inline-block h-2 w-2 rounded-full bg-status-warning" />
                                                ) : null}
                                            </span>
                                        ),
                                        children: (
                                            <div className="px-6 py-4">
                                                <Form<FormValues>
                                                    form={form}
                                                    id={`review-classification-${id}`}
                                                    layout="vertical"
                                                    onFinish={handleSubmit}
                                                    onValuesChange={markDeferredReviewEdited}
                                                    disabled={!reviewEditingEnabled}
                                                    className="flex flex-col gap-3"
                                                >
                                                    <Card
                                                        title={
                                                            <span className="flex items-center gap-2">
                                                                <SlidersHorizontal
                                                                    size={14}
                                                                    className="text-muted-foreground"
                                                                />
                                                                Override classification
                                                            </span>
                                                        }
                                                        size="small"
                                                    >
                                                        <Row gutter={16}>
                                                            <Col span={8}>
                                                                <FormItem
                                                                    name="isIncident"
                                                                    label="Incident"
                                                                    valuePropName="checked"
                                                                    className="!mb-0"
                                                                >
                                                                    <Switch
                                                                        checkedChildren="Yes"
                                                                        unCheckedChildren="No"
                                                                    />
                                                                </FormItem>
                                                            </Col>
                                                            <Col span={8}>
                                                                <FormItem
                                                                    name="isComplaint"
                                                                    label="Complaint"
                                                                    valuePropName="checked"
                                                                    className="!mb-0"
                                                                >
                                                                    <Switch
                                                                        checkedChildren="Yes"
                                                                        unCheckedChildren="No"
                                                                    />
                                                                </FormItem>
                                                            </Col>
                                                            <Col span={8}>
                                                                <FormItem
                                                                    name="confidence"
                                                                    label="Certainty"
                                                                    rules={[
                                                                        {
                                                                            required: true,
                                                                            message: 'Please select a certainty level',
                                                                        },
                                                                    ]}
                                                                    className="!mb-0"
                                                                >
                                                                    <Select
                                                                        placeholder="Select certainty"
                                                                        options={Object.entries(CONFIDENCE_MAP).map(
                                                                            ([value, { label, color }]) => ({
                                                                                label: (
                                                                                    <Tag
                                                                                        variant="outlined"
                                                                                        color={color}
                                                                                        className="m-0"
                                                                                    >
                                                                                        {label}
                                                                                    </Tag>
                                                                                ),
                                                                                value,
                                                                            }),
                                                                        )}
                                                                    />
                                                                </FormItem>
                                                            </Col>
                                                        </Row>
                                                    </Card>

                                                    <Card
                                                        title={
                                                            <span className="flex items-center gap-2">
                                                                <ShieldAlert
                                                                    size={14}
                                                                    className="text-muted-foreground"
                                                                />
                                                                Regulatory breach
                                                            </span>
                                                        }
                                                        size="small"
                                                    >
                                                        <div className="mb-3 flex items-center gap-3">
                                                            <FormItem
                                                                name="isBreachOfRegulation"
                                                                valuePropName="checked"
                                                                className="!mb-0"
                                                            >
                                                                <Switch checkedChildren="Yes" unCheckedChildren="No" />
                                                            </FormItem>
                                                            <span
                                                                className={
                                                                    isBreachOfRegulation
                                                                        ? 'font-medium text-sm text-status-danger'
                                                                        : 'text-foreground text-sm'
                                                                }
                                                            >
                                                                This message indicates a breach of regulation
                                                            </span>
                                                        </div>
                                                        <AiSuggestionAlert
                                                            text={aiBreachText}
                                                            onUse={useAiBreachText}
                                                        />
                                                        <FormItem
                                                            name="breachedRegulation"
                                                            label="Breached regulation details"
                                                            className="!mb-0"
                                                        >
                                                            <TextAreaWithAiAssistant
                                                                autoSize={{ minRows: 3, maxRows: 6 }}
                                                                placeholder="Describe the specific regulation that was breached..."
                                                                relatedFieldLabel="Email Classification - Breached Regulation"
                                                                onChange={markBreachDetailsEdited}
                                                            />
                                                        </FormItem>
                                                    </Card>

                                                    <Card
                                                        title={
                                                            <span className="flex items-center gap-2">
                                                                <MessageSquareText
                                                                    size={14}
                                                                    className="text-muted-foreground"
                                                                />
                                                                Justification
                                                            </span>
                                                        }
                                                        size="small"
                                                    >
                                                        <AiSuggestionAlert
                                                            text={aiJustification}
                                                            onUse={useAiJustification}
                                                        />
                                                        <FormItem
                                                            name="justification"
                                                            label="Provide your reasoning for this classification"
                                                            className="!mb-0"
                                                        >
                                                            <TextAreaWithAiAssistant
                                                                autoSize={{ minRows: 3, maxRows: 6 }}
                                                                placeholder="Explain why you classified this email as shown above..."
                                                                relatedFieldLabel="Email Classification - Justification"
                                                                onChange={markJustificationEdited}
                                                            />
                                                        </FormItem>
                                                    </Card>

                                                    <Card
                                                        title={
                                                            <span className="flex items-center gap-2">
                                                                <TagsIcon size={14} className="text-muted-foreground" />
                                                                Tags
                                                            </span>
                                                        }
                                                        size="small"
                                                    >
                                                        <FormItem
                                                            name="tags"
                                                            label="Add relevant tags for better organisation"
                                                            className="!mb-0"
                                                        >
                                                            <Select
                                                                mode="tags"
                                                                placeholder="Type to add tags (press Enter or comma to confirm)"
                                                                options={(availableTags || []).map((tag: string) => ({
                                                                    label: tag,
                                                                    value: tag,
                                                                }))}
                                                                tokenSeparators={[',']}
                                                            />
                                                        </FormItem>
                                                    </Card>

                                                    <ReviewerDisagreementSection
                                                        differingFields={differingFields}
                                                        feedback={feedback}
                                                        onChange={handleFeedbackChange}
                                                    />
                                                </Form>
                                            </div>
                                        ),
                                    },
                                    ...(enableDebugInfo
                                        ? [
                                              {
                                                  key: 'debug' as const,
                                                  label: 'Debug',
                                                  children: (
                                                      <div className="px-6 py-4">
                                                          <CollapsibleCard
                                                              title="Debug information"
                                                              isCollapsible={true}
                                                              isDefaultExpanded={true}
                                                          >
                                                              <div className="p-4">
                                                                  {data.aiResponse?.traceId ? (
                                                                      <div className="mb-4 rounded-lg border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-violet-50 p-3 dark:border-blue-700 dark:from-blue-950/50 dark:to-violet-950/50">
                                                                          <div className="flex items-start gap-2">
                                                                              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500">
                                                                                  <span className="font-bold text-white text-xs">
                                                                                      #
                                                                                  </span>
                                                                              </div>
                                                                              <div className="flex-1">
                                                                                  <span className="font-bold text-blue-900 text-sm dark:text-blue-300">
                                                                                      Trace ID
                                                                                  </span>
                                                                                  <code className="mt-1 block select-all rounded border border-blue-200 bg-card px-2 py-1 font-mono text-blue-700 text-xs dark:border-blue-700 dark:text-blue-300">
                                                                                      {data.aiResponse.traceId}
                                                                                  </code>
                                                                              </div>
                                                                          </div>
                                                                      </div>
                                                                  ) : null}
                                                                  <div className="rounded-lg border border-separator bg-muted p-3">
                                                                      <JsonView
                                                                          value={data}
                                                                          displayDataTypes={false}
                                                                          displayObjectSize={false}
                                                                          enableClipboard={false}
                                                                          collapsed={1}
                                                                      />
                                                                  </div>
                                                              </div>
                                                          </CollapsibleCard>
                                                      </div>
                                                  ),
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                        </div>
                    </div>
                ) : null}
            </Drawer>
            <SkipDialog
                open={skipOpen}
                onCancel={() => setSkipOpen(false)}
                onSubmit={handleSkipSubmit}
                submitting={isSkipping}
                disabled={!canSkip || isTeachSettling}
            />
            {enableClassificationLearning ? (
                <RecheckSimilarModal
                    target={recheckTarget}
                    onClose={() => {
                        setRecheckTarget(null);
                        // The submit branches hand their deferred navigation to the offer.
                        const after = afterRecheckRef.current;
                        afterRecheckRef.current = null;
                        if (after?.kind === 'next') {
                            navigateToId(after.nextId);
                        } else if (after?.kind === 'close') {
                            setOpen(false);
                        }
                    }}
                />
            ) : null}
        </PageController>
    );
}

function EmailReferencePanel({ data, orgId: _orgId }: { data: Data; orgId: bigint }) {
    const { formatDateTime } = useFormatter();
    return (
        <div className="flex min-h-0 flex-col border-separator border-r">
            <div className="border-separator border-b bg-muted px-6 py-4">
                <div className="flex items-center gap-3">
                    <div
                        className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                            data.messageType === 'SMS' ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                    >
                        {data.messageType === 'SMS' ? (
                            <MessageSquare className="h-5 w-5 text-white" />
                        ) : (
                            <Mail className="h-5 w-5 text-white" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-base text-foreground">
                            {data.messageType === 'SMS' ? 'SMS content' : 'Email content'}
                        </h3>
                        <p className="text-muted-foreground text-sm">Reference while reviewing</p>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-auto p-6">
                {data.messageType === 'SMS' ? (
                    <div className="space-y-6">
                        <div className="rounded-lg border border-separator bg-card p-4">
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                                {data.senderPhone && (
                                    <>
                                        <span className="font-medium text-subtle-foreground">From:</span>
                                        <span className="font-medium text-foreground">{data.senderPhone}</span>
                                    </>
                                )}
                                {data.recipientPhone && (
                                    <>
                                        <span className="font-medium text-subtle-foreground">To:</span>
                                        <span className="text-foreground">{data.recipientPhone}</span>
                                    </>
                                )}
                                {data.messageDate && (
                                    <>
                                        <span className="font-medium text-subtle-foreground">Date:</span>
                                        <span className="text-foreground">{formatDateTime(data.messageDate)}</span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="rounded-lg border border-separator bg-card p-6">
                            <h4 className="mb-4 font-semibold text-muted-foreground text-sm uppercase tracking-wider">
                                Message content
                            </h4>
                            <div className="whitespace-pre-wrap text-foreground">{data.bodyText}</div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {data.subject && (
                            <div className="rounded-lg border border-separator bg-card p-4">
                                <h3 className="font-semibold text-foreground text-lg">{data.subject}</h3>
                            </div>
                        )}
                        <div className="rounded-lg border border-separator bg-card p-4">
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                                {data.from && (
                                    <>
                                        <span className="font-medium text-subtle-foreground">From:</span>
                                        <span className="font-medium text-foreground">
                                            {formatEmailAddresses(data.from)}
                                        </span>
                                    </>
                                )}
                                {data.to && (
                                    <>
                                        <span className="font-medium text-subtle-foreground">To:</span>
                                        <span className="text-foreground">{formatEmailAddresses(data.to)}</span>
                                    </>
                                )}
                                {data.cc && formatEmailAddresses(data.cc) && (
                                    <>
                                        <span className="font-medium text-subtle-foreground">Cc:</span>
                                        <span className="text-foreground">{formatEmailAddresses(data.cc)}</span>
                                    </>
                                )}
                                {data.bcc && formatEmailAddresses(data.bcc) && (
                                    <>
                                        <span className="font-medium text-subtle-foreground">Bcc:</span>
                                        <span className="text-foreground">{formatEmailAddresses(data.bcc)}</span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="rounded-lg border border-separator bg-card p-6">
                            <h4 className="mb-4 font-semibold text-muted-foreground text-sm uppercase tracking-wider">
                                Message content
                            </h4>
                            {data.bodyHtml ? (
                                <EmailContentRenderer html={sanitizeHtml(data.bodyHtml)} />
                            ) : (
                                <div className="whitespace-pre-wrap text-foreground">{data.bodyText}</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function buildLinkedClaimDecisionPayload(
    data: Data,
    localDecisions: ClaimDecisionState,
): ReviewInput['linkedClaimDecisions'] {
    // `claimSources` enumerates every claim the route's replace-all
    // semantics could touch: currently-visible matched + inferred claims
    // PLUS every server-persisted decision (filtered to readable on the
    // get-side). Omitting a readable persisted decision here would cause
    // `reviewMessageClassification` to silently delete it on the next
    // submit — see #1 in the M1+M2 review for the trigger scenario.
    const claimSources = new Map<string, MessageClassificationClaimLinkSource>();
    for (const claim of data.matchedClaims) {
        claimSources.set(claim.id, 'DETECTED');
    }
    for (const claim of data.inferredFromSenderClaims) {
        if (!claimSources.has(claim.id)) {
            claimSources.set(claim.id, 'INFERRED_FROM_SENDER');
        }
    }
    const serverDecisionsByClaimId = new Map(data.linkedClaimDecisions.map((decision) => [decision.claimId, decision]));
    for (const [claimId, decision] of serverDecisionsByClaimId) {
        if (!claimSources.has(claimId)) {
            claimSources.set(claimId, decision.source);
        }
    }

    return [...claimSources.entries()].flatMap(([claimId, defaultSource]) => {
        const localDecision = localDecisions[claimId];
        if (localDecision) {
            return localDecision.decision
                ? [{ claimId: BigInt(claimId), source: localDecision.source, decision: localDecision.decision }]
                : [];
        }

        const serverDecision = serverDecisionsByClaimId.get(claimId);
        if (serverDecision?.confirmed) {
            return [{ claimId: BigInt(claimId), source: serverDecision.source ?? defaultSource, decision: 'CONFIRM' }];
        }
        if (serverDecision?.rejected) {
            return [{ claimId: BigInt(claimId), source: serverDecision.source ?? defaultSource, decision: 'REJECT' }];
        }
        return [];
    });
}

/**
 * AI-suggested text callout rendered inline above the matching free-text
 * field (breach details, justification). Returns null when the AI did
 * not supply text so callers don't need to gate.
 */
function AiSuggestionAlert({ text, onUse }: { text: string; onUse: () => void }) {
    if (!text) return null;
    return (
        <Alert
            type="info"
            className="!mb-3"
            message={
                <div className="flex items-start gap-2">
                    <Sparkles size={14} className="mt-0.5 shrink-0 text-accent-500" />
                    <div className="flex flex-1 flex-col gap-2">
                        <div className="text-xs">
                            <strong>AI suggested:</strong> {text}
                        </div>
                        <div className="flex justify-end">
                            <Button type="tertiary" size="small" onClick={onUse}>
                                Use AI reasoning
                            </Button>
                        </div>
                    </div>
                </div>
            }
        />
    );
}

function boolAsString(value: boolean | null | undefined): string | null {
    if (value === true) return 'true';
    if (value === false) return 'false';
    return null;
}

function aiValueForField(aiOriginalClassification: AiOriginalClassification, field: FeedbackField): string | null {
    switch (field) {
        case 'isIncident':
            return boolAsString(aiOriginalClassification.isIncident);
        case 'isComplaint':
            return boolAsString(aiOriginalClassification.isComplaint);
        case 'confidence':
            return aiOriginalClassification.confidence;
        case 'isBreachOfRegulation':
            return boolAsString(aiOriginalClassification.isBreachOfRegulation);
    }
}

function reviewerValueForField(
    form: ReturnType<typeof Form.useForm<FormValues>>[0],
    isBreachOfRegulation: boolean | undefined,
    field: FeedbackField,
): string | null {
    switch (field) {
        case 'isIncident':
            return boolAsString(form.getFieldValue('isIncident'));
        case 'isComplaint':
            return boolAsString(form.getFieldValue('isComplaint'));
        case 'confidence':
            return form.getFieldValue('confidence') ?? null;
        case 'isBreachOfRegulation':
            return boolAsString(isBreachOfRegulation);
    }
}

/**
 * Detects whether focus is inside an editable element so we can mute
 * single-key shortcuts (j/k/y/n/b/1/2/3) while typing. Mod+Enter and
 * Escape stay active in textareas — they're never something the user
 * types accidentally.
 */
function useEditableFocusDetector(): boolean {
    const [isEditable, setIsEditable] = useState(false);
    useEffect(() => {
        function check() {
            const el = document.activeElement as HTMLElement | null;
            if (!el) {
                setIsEditable(false);
                return;
            }
            const tag = el.tagName?.toLowerCase();
            const editable =
                tag === 'input' ||
                tag === 'textarea' ||
                tag === 'select' ||
                el.isContentEditable ||
                el.getAttribute('role') === 'combobox' ||
                Boolean(el.closest('[role="radiogroup"]'));
            setIsEditable(editable);
        }
        check();
        const handler = () => check();
        document.addEventListener('focusin', handler);
        document.addEventListener('focusout', handler);
        return () => {
            document.removeEventListener('focusin', handler);
            document.removeEventListener('focusout', handler);
        };
    }, []);
    return isEditable;
}
